import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

export function Portfolio() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/portfolio").then(r => setItems(r.data)); }, []);
  useSEO({
    title: "Portfolio",
    description: "Selected case work across printing, graphic design, and website craft.",
    breadcrumbs: [{ name: "Home", url: "/" }, { name: "Portfolio", url: "/portfolio" }],
  });
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Portfolio</p>
      <h1 className="font-serif text-5xl md:text-7xl mt-3">Case work.</h1>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(p => (
          <div key={p.id} className="group border border-[#E5E5E5] overflow-hidden bg-[#FAFAFA]" data-testid={`portfolio-${p.id}`}>
            <div className="aspect-[4/5] overflow-hidden"><img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
            <div className="p-6">
              <p className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#C5A059]">{p.category.replace("_"," ")}</p>
              <h3 className="font-serif text-2xl mt-2">{p.title}</h3>
              <p className="text-sm text-[#555] mt-2">{p.description}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Blog() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then(r => setPosts(r.data)); }, []);
  useSEO({
    title: "Journal",
    description: "Notes from the press — printing tips, design craft, and studio updates.",
    breadcrumbs: [{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }],
  });
  return (
    <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-24">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Journal</p>
      <h1 className="font-serif text-5xl md:text-7xl mt-3">Notes from the press.</h1>
      <div className="mt-16 space-y-8">
        {posts.map(p => (
          <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-${p.slug}`} className="group grid md:grid-cols-3 gap-6 border-t border-[#E5E5E5] pt-8">
            <div className="aspect-[4/3] overflow-hidden bg-[#FAFAFA]"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
            <div className="md:col-span-2">
              <p className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#C5A059]">{new Date(p.created_at).toLocaleDateString()}</p>
              <h3 className="font-serif text-3xl mt-2 group-hover:text-[#C5A059] transition-colors">{p.title}</h3>
              <p className="text-[#555] mt-3">{p.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function BlogPost({ slug }) {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/blog/${slug}`).then(r => setP(r.data)).catch(() => setP({title:"Not found",content:"",_missing:true})); }, [slug]);
  useSEO(p ? {
    title: p.title,
    description: p.excerpt || (p.content || "").slice(0, 155),
    image: p.cover_image,
    type: "article",
    noIndex: p._missing,
    breadcrumbs: [{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }, { name: p.title, url: `/blog/${slug}` }],
    jsonLd: p._missing ? null : {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: p.title,
      description: p.excerpt || "",
      image: p.cover_image ? [p.cover_image] : undefined,
      datePublished: p.created_at,
      dateModified: p.updated_at || p.created_at,
      author: { "@type": "Organization", name: "BOLAWPRESS" },
      publisher: { "@type": "Organization", name: "BOLAWPRESS" },
      mainEntityOfPage: { "@type": "WebPage", "@id": typeof window !== "undefined" ? window.location.href : "" },
    },
  } : {});
  if (!p) return <div className="p-20 text-center font-mono text-xs">LOADING...</div>;
  return (
    <article className="max-w-[820px] mx-auto px-6 py-24">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">{new Date(p.created_at).toLocaleDateString()}</p>
      <h1 className="font-serif text-4xl md:text-6xl mt-3">{p.title}</h1>
      {p.cover_image && <img src={p.cover_image} className="w-full mt-10 aspect-video object-cover" alt={p.title} />}
      <div className="mt-10 text-lg leading-relaxed text-[#333] whitespace-pre-wrap">{p.content}</div>
    </article>
  );
}

export function CMSPage({ slug }) {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/cms/pages/${slug}`).then(r => setP(r.data)).catch(() => setP({title:"Not found",content:"",_missing:true})); }, [slug]);
  useSEO(p ? {
    title: p.title,
    description: p.meta_description || (p.content || "").slice(0, 155),
    noIndex: p._missing,
    breadcrumbs: [{ name: "Home", url: "/" }, { name: p.title, url: window.location.pathname }],
  } : {});
  if (!p) return <div className="p-20 text-center font-mono text-xs">LOADING...</div>;
  return (
    <article className="max-w-[820px] mx-auto px-6 py-24">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">BOLAWPRESS</p>
      <h1 className="font-serif text-4xl md:text-6xl mt-3">{p.title}</h1>
      <div className="mt-10 text-lg leading-relaxed text-[#333] whitespace-pre-wrap">{p.content}</div>
    </article>
  );
}
